import React from "react";
import { useAuth } from "../../context/AuthContext";
import { FaUserCircle, FaRegBell, FaBell, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  Flex,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  IconButton,
  InputGroup,
  InputLeftElement,
  Input,
  Image,
  Badge,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useTrialStatus } from "../../context/TrialStatusContext";
import BusinessUnitSwitcher from "../BusinessUnitSwitcher/BusinessUnitSwitcher";
import { isSuperAdmin, convertLegacyRole } from "../../constants/roles";
import { useTranslation } from "react-i18next";
import { useNotifications } from "../../context/NotificationsContext";
import NotificationsDrawer from "../Notifications/NotificationsDrawer";
import { useBusinessUnit } from "../../context/BusinessUnitContext";

const PortalHeader = () => {
  const { user, logout } = useAuth();
  const { isConsultantPortal } = useBusinessUnit();
  const userRole = convertLegacyRole(user?.role);
  const isSA = isSuperAdmin(userRole);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("portal");
  const { unreadCount, openNotifications } = useNotifications();

  // State for company info
  const [companyName, setCompanyName] = React.useState("");
  const [logoUrl, setLogoUrl] = React.useState("");
  const { refreshStatus, accountStatus, trialDaysRemaining } = useTrialStatus();
  const [profileName, setProfileName] = React.useState("");

  // Fetch company details once (on mount)
  React.useEffect(() => {
    let mounted = true;
    const fetchCompany = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/company/basic`);
        if (!mounted) return;
        setCompanyName(res.data?.company_name || "");
        setLogoUrl(res.data?.logo_url || "");
        refreshStatus?.();
      } catch (err) {
        console.error("Failed to fetch company details", err);
      }
    };
    fetchCompany();
    return () => {
      mounted = false;
    };
  }, [refreshStatus]);

  // Fetch user profile to retrieve first and last name the same way as Profile Settings
  React.useEffect(() => {
    let mounted = true;
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/users/me`);
        if (!mounted) return;
        const data = res.data || {};
        const first = data.first_name || data.firstName || "";
        const last = data.last_name || data.lastName || "";
        const name = `${first} ${last}`.trim();
        setProfileName(name);
      } catch (err) {
        // non-blocking: leave profileName empty if it fails
      }
    };
    fetchProfile();
    return () => {
      mounted = false;
    };
  }, []);

  const fullName =
    profileName || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "Account";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const goToProfile = () => {
    navigate("/portal/profile");
  };

  const handleChangeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    try {
      window.localStorage.setItem("portalLanguage", lang);
    } catch {
      // ignore storage errors
    }
    // Optional: set for immediate effect even before next request interceptor run
    axios.defaults.headers.common["Accept-Language"] = lang;
  };

  const currentLang = i18n.language === "fr" ? "fr" : "en";

  return (
    <Flex
      as="header"
      position="fixed"
      top="0"
      left="0"
      w="100%"
      h="54px"
      px={4}
      align="center"
      bg="rgba(255,255,255,0.94)"
      backdropFilter="blur(14px)"
      borderBottomWidth="1px"
      borderColor="portal.line"
      boxShadow="brandbar"
      zIndex="1000"
    >
      {/* Left section */}
      <Flex align="center" minW="200px" gap={2}>
        {logoUrl && (
          <Image src={logoUrl} alt={companyName} h="34px" objectFit="contain" />
        )}
        <Box
          fontWeight="800"
          fontSize="17px"
          letterSpacing="-0.03em"
          color="portal.text"
          whiteSpace="nowrap"
        >
          {companyName || user?.companyCode || ""}
        </Box>
        {!isConsultantPortal && <BusinessUnitSwitcher />}
        {accountStatus === "TRIAL" && typeof trialDaysRemaining === "number" && (
          <Badge colorScheme={trialDaysRemaining <= 3 ? "purple" : "gray"} ml={2} borderRadius="md">
            {t("header.trialDays", { count: trialDaysRemaining })}
          </Badge>
        )}
      </Flex>

      {/* Center search */}
      {isConsultantPortal ? <Box flex="1" /> : (
        <Box flex="1" maxW="420px" mx="auto">
          <InputGroup>
            <InputLeftElement h="34px" pointerEvents="none" color="portal.muted">
              <FaSearch size="0.85em" />
            </InputLeftElement>
            <Input
              type="text"
              placeholder={t("header.searchPlaceholder")}
              h="34px"
              bg="portal.soft"
              borderColor="portal.line"
              fontWeight="500"
              _placeholder={{ color: "portal.muted" }}
            />
          </InputGroup>
        </Box>
      )}

      {/* Right section: Language, notifications and user menu */}
      <Flex align="center" gap={3}>
        <Menu>
          <MenuButton
            as={Button}
            variant="ghost"
            size="md"
            rightIcon={<ChevronDownIcon />}
            color="black"
            aria-label={t("header.language")}
          >
            {currentLang === "fr" ? t("header.language_fr") : t("header.language_en")}
          </MenuButton>
          <MenuList>
            <MenuItem onClick={() => handleChangeLanguage("en")}>{t("header.language_en")}</MenuItem>
            <MenuItem onClick={() => handleChangeLanguage("fr")}>{t("header.language_fr")}</MenuItem>
          </MenuList>
        </Menu>
        {!isConsultantPortal && (
          <Box position="relative">
            <Tooltip label={t("header.notifications", { defaultValue: "Notifications" })}>
              <IconButton
                icon={Number(unreadCount) > 0 ? <FaBell /> : <FaRegBell />}
                variant="outline"
                size="sm"
                h="34px"
                w="34px"
                bg="white"
                borderColor="portal.line"
                borderRadius="10px"
                color={Number(unreadCount) > 0 ? "portal.primary" : "portal.muted"}
                aria-label={t("header.notifications", { defaultValue: "Notifications" })}
                _hover={{ bg: "portal.active", color: "portal.primary" }}
                onClick={openNotifications}
              />
            </Tooltip>
            {Number(unreadCount) > 0 && (
              <Box
                position="absolute"
                top="2px"
                right="4px"
                w="8px"
                h="8px"
                bg="portal.mint"
                borderRadius="full"
                border="2px solid white"
              />
            )}
            {Number(unreadCount) > 0 && (
              <Badge
                position="absolute"
                top="-4px"
                right="-6px"
                borderRadius="full"
                px={2}
                fontSize="xs"
                colorScheme="red"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
          </Box>
        )}
        <Menu>
          <MenuButton
            as={Button}
            leftIcon={<FaUserCircle />}
            rightIcon={<ChevronDownIcon />}
            variant="outline"
            size="sm"
            h="34px"
            bg="white"
            borderColor="portal.line"
            borderRadius="10px"
            color="portal.text"
            fontWeight="700"
            aria-label="User menu"
            _hover={{ bg: "portal.soft" }}
            _active={{ bg: "portal.soft" }}
          >
            {fullName}
          </MenuButton>
          <MenuList>
            {isSA && <MenuItem onClick={() => navigate("/admin")}>{t("header.superAdmin")}</MenuItem>}
            <MenuItem onClick={goToProfile}>{t("header.profile")}</MenuItem>
            <MenuItem onClick={handleLogout}>{t("header.logout")}</MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      {!isConsultantPortal && <NotificationsDrawer />}
    </Flex>
  );
};

export default PortalHeader; 
